// SPDX-License-Identifier: AGPL-3.0-or-later

import type {GuildRole} from '@app/features/guild/models/GuildRole';
import {Tooltip} from '@app/features/ui/tooltip/Tooltip';
import * as AvatarUtils from '@app/features/user/utils/AvatarUtils';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import styles from './RoleIcon.module.css';

interface RoleIconProps {
	role: GuildRole;
	size?: number;
	className?: string;
}

export const RoleIcon: React.FC<RoleIconProps> = observer(({role, size = 18, className}) => {
	if (!role.icon && !role.unicodeEmoji) {
		return null;
	}

	if (role.icon) {
		const iconUrl =
			role.icon.startsWith('data:') || role.icon.startsWith('blob:') || role.icon.startsWith('http')
				? role.icon
				: AvatarUtils.getRoleIconURL({id: role.id, icon: role.icon}, false, size * 2);

		return (
			<Tooltip text={role.name} data-flx="guild.role-icon.tooltip">
				<span
					className={clsx(styles.roleIconWrapper, className)}
					style={{width: size, height: size}}
					data-flx="guild.role-icon.wrapper"
				>
					<img
						src={iconUrl}
						alt={role.name}
						className={styles.roleIconImg}
						width={size}
						height={size}
						loading="lazy"
						data-flx="guild.role-icon.img"
					/>
				</span>
			</Tooltip>
		);
	}

	if (role.unicodeEmoji) {
		return (
			<Tooltip text={role.name} data-flx="guild.role-icon.tooltip">
				<span
					className={clsx(styles.roleIconWrapper, styles.roleIconEmoji, className)}
					style={{width: size, height: size, fontSize: `${Math.round(size * 0.9)}px`}}
					aria-label={role.name}
					data-flx="guild.role-icon.emoji"
				>
					{role.unicodeEmoji}
				</span>
			</Tooltip>
		);
	}

	return null;
});
